import { ExternalLink, GitBranch } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { EstadoErro, EstadoVazio } from '@/components/patterns/states';
import { DetalheItem, DetalheLista } from '@/components/patterns/metric';
import { useAcesso, usePodeVer } from '@/hooks/use-acesso';
import { IncidenteSelo } from '@/components/patterns/incidente-badges';
import { NoPainelBadge, StatusBuildBadge } from '@/components/patterns/status-badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { dataSource } from '@/data';
import { CompilacoesHttpError } from '@/data/http';
import type { BuildDetalhe } from '@/data/types';
import { useEventos } from '@/hooks/use-eventos';
import { useResource } from '@/hooks/use-resource';
import { formatarDataHora, formatarRelativo, texto } from '@/lib/format';
import { cn } from '@/lib/utils';
import { IncidentesDaVersao } from '@/features/incidentes/IncidentesDaVersao';
import { AcoesBuild } from './acoes-build';
import { DownloadLinks } from './download-links';
import { LinhaDoTempo } from './linha-do-tempo';
import { Manifesto } from './manifesto';

/**
 * Detalhe de uma versão — o mesmo componente na página própria
 * (`/versoes/:versao`) e no Sheet lateral aberto a partir do feed. Quem monta
 * decide só o elemento do título (`<h1>` na página, `SheetTitle` no Sheet).
 */
export function BuildDetalheView({
  versao,
  produto,
  titulo,
}: {
  versao: string;
  produto?: string;
  /** Renderiza o título acessível da view (h1 ou SheetTitle). */
  titulo: (texto: string, className: string) => React.ReactNode;
}) {
  const recurso = useResource((signal) => dataSource.obterBuild(versao, produto, signal), [versao, produto]);
  const { definir } = recurso;

  // Publicação no painel de OUTRA versão (pela tela do painel) muda o selo
  // desta: relê em silêncio.
  useEventos((evento) => {
    if (evento.tipo === 'build' && evento.build.versao === versao) definir(evento.build);
    if (evento.tipo === 'painel') recurso.atualizarEmSilencio();
    // Incidente registrado/associado/mudado (aqui ou no Sheet por cima): relê a lista e o aviso.
    if (evento.tipo === 'incidente' && evento.versoes.includes(versao)) recurso.atualizarEmSilencio();
  });

  if (recurso.estado === 'carregando' && !recurso.dados) return <DetalheCarregando />;

  if (recurso.estado === 'erro' || !recurso.dados) {
    if (recurso.erro instanceof CompilacoesHttpError && recurso.erro.status === 404) {
      return (
        <>
          {titulo(versao, 'sr-only')}
          <EstadoVazio
            titulo={`Versão ${versao} não encontrada`}
            descricao="Confira o número da versão. Só aparecem aqui as versões que o Jenkins publicou no Compilações."
            acao={
              <Link to="/feed" className="text-body font-bold text-primary underline-offset-4 hover:underline">
                Voltar ao feed
              </Link>
            }
          />
        </>
      );
    }
    return (
      <>
        {titulo(versao, 'sr-only')}
        <EstadoErro erro={recurso.erro} aoTentarDeNovo={recurso.recarregar} />
      </>
    );
  }

  return <Conteudo build={recurso.dados} titulo={titulo} aoAtualizar={definir} />;
}

function Conteudo({
  build,
  titulo,
  aoAtualizar,
}: {
  build: BuildDetalhe;
  titulo: (texto: string, className: string) => React.ReactNode;
  aoAtualizar: (detalhe: BuildDetalhe) => void;
}) {
  const { colaborador } = useAcesso();
  const verRamos = usePodeVer('ramos');
  const verIncidentes = usePodeVer('incidentes');
  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <p className="cmp-eyebrow text-primary">Versão</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {titulo(build.versao, 'font-mono text-page-title tabular-nums')}
          <StatusBuildBadge status={build.status} />
          {build.no_painel ? <NoPainelBadge /> : null}
          <IncidenteSelo
            quantidade={build.incidentes_abertos}
            severidade={build.incidente_severidade_max}
            versao={build.versao}
            aoClicar={() =>
              document
                .getElementById('titulo-incidentes-versao')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          />
        </div>
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body text-on-surface-muted">
          <GitBranch className="size-4 text-icon" aria-hidden="true" />
          <span className="font-mono text-on-surface">{build.branch}</span>
          <span aria-hidden="true">·</span>
          {verRamos ? (
            <Link
              to={`/ramos/${encodeURIComponent(build.ramo)}`}
              className="text-primary underline-offset-4 hover:underline"
            >
              Ramo {build.ramo}
            </Link>
          ) : (
            <span>Ramo {build.ramo}</span>
          )}
          <span aria-hidden="true">·</span>
          <span>
            Publicada{' '}
            <time dateTime={build.publicado_em} title={formatarDataHora(build.publicado_em)}>
              {formatarRelativo(build.publicado_em)}
            </time>
          </span>
        </p>
      </header>

      <AcoesBuild build={build} aoAtualizar={aoAtualizar} />

      <div className={cn('grid gap-4', colaborador ? 'lg:grid-cols-3' : 'lg:grid-cols-2')}>
        <Card flat>
          <CardHeader className="py-3">
            <CardTitle className="text-body">Downloads</CardTitle>
          </CardHeader>
          <CardContent className="gap-3 p-4">
            <DownloadLinks links={build.links} versao={build.versao} className="flex-col items-stretch" />
            <p className="text-cell text-on-surface-muted">{build.total_artefatos} artefatos no manifesto.</p>
          </CardContent>
        </Card>

        <Card flat>
          <CardHeader className="py-3">
            <CardTitle className="text-body">Homologação</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <DetalheLista className="grid-cols-1 gap-y-3">
              <DetalheItem rotulo="Status">
                <StatusBuildBadge status={build.status} />
              </DetalheItem>
              {build.status_motivo ? <DetalheItem rotulo="Motivo">{build.status_motivo}</DetalheItem> : null}
              <DetalheItem rotulo="Avaliada por">
                {build.status === 'nao_homologado' && !build.status_por
                  ? 'Ainda não avaliada'
                  : texto(build.status_por)}
              </DetalheItem>
              {build.status_em ? <DetalheItem rotulo="Em">{formatarDataHora(build.status_em)}</DetalheItem> : null}
            </DetalheLista>
          </CardContent>
        </Card>

        {colaborador ? (
          <Card flat>
            <CardHeader className="py-3">
              <CardTitle className="text-body">Jenkins</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {build.jenkins ? (
                <DetalheLista className="grid-cols-1 gap-y-3">
                  <DetalheItem rotulo="Job">{texto(build.jenkins.job)}</DetalheItem>
                  <DetalheItem rotulo="Build">
                    {build.jenkins.build != null ? `#${build.jenkins.build}` : texto(null)}
                  </DetalheItem>
                  {build.jenkins.url ? (
                    <DetalheItem rotulo="Execução">
                      <a
                        href={build.jenkins.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 break-all text-primary underline-offset-4 hover:underline"
                      >
                        Abrir no Jenkins
                        <ExternalLink className="size-3.5 shrink-0" aria-hidden="true" />
                        <span className="sr-only">(abre em nova aba)</span>
                      </a>
                    </DetalheItem>
                  ) : null}
                </DetalheLista>
              ) : (
                <p className="text-body text-on-surface-muted">
                  Sem metadados do Jenkins — versão importada do WordPress.
                </p>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>

      {verIncidentes ? <IncidentesDaVersao build={build} /> : null}

      <Manifesto artefatos={build.artefatos} diff={build.diff_anterior} />

      <section className="flex flex-col gap-4" aria-labelledby="titulo-linha-do-tempo">
        <h2 id="titulo-linha-do-tempo" className="text-section-title">
          Linha do tempo
        </h2>
        <LinhaDoTempo eventos={build.eventos} />
      </section>
    </div>
  );
}

function DetalheCarregando({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-col gap-6', className)} role="status" aria-label="Carregando a versão">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-8 w-28" />
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-8 w-24" />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Skeleton className="h-36" />
        <Skeleton className="h-36" />
        <Skeleton className="h-36" />
      </div>
      <Skeleton className="h-72" />
    </div>
  );
}
